import type { ReactNode } from 'react';
import type { SubmissionSummary } from '../api/useSubmissions.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Pille } from '../status/Pille.tsx';
import { statusEtikett } from '../status/status.ts';
import { saksnr } from './saksnummer.ts';


export function alder(fra: string, nå: Date): string {
  const dager = Math.floor((nå.getTime() - new Date(fra).getTime()) / 86_400_000);
  if (dager <= 0) return 'i dag';
  if (dager < 30) return `${dager} d`;
  return `${Math.floor(dager / 30)} mnd`;
}

export function dagerTil(til: string, nå: Date): number {
  return Math.ceil((new Date(til).getTime() - nå.getTime()) / 86_400_000);
}

/** Fristdatoen som radens tall: «30. sep» — dag og kort måned, uten punktumet nb-NO setter på måneden. */
export function kortDato(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }).replace(/\.$/, '');
}

const SPOR = { feil: { ikon: 'bug-outline', navn: 'Feil' }, onske: { ikon: 'bulb-outline', navn: 'Ønske' } } as const;

/** Reach er distinkte innsendere MED identitet; en anonym innsending teller aldri (CLAUDE.md, «Identitet»). «0 har spurt» leste som en feil i lista. */
export function reachTekst(reach: number): string {
  return reach > 0 ? `${reach} har spurt` : 'anonym';
}

/** Rotstien «/» sier ingenting — «forsiden» gjør. Andre skjermnavn vises som de er. */
export function skjermNavn(screen: string | undefined): string | undefined {
  return screen === '/' ? 'forsiden' : screen;
}

export const INAKTIV_DAGER = 28;

interface Ledd {
  navn: string;
  innhold: ReactNode;
  klasse?: string;
  testid?: string;
}

/**
 * Raden (R1.3 — DD-59, DD-61, DD-67, DD-69; innboks-konsept v0.2, `admin-innboks.jsx`):
 *
 *   [sporikon] [tittel, body, halvfet]              [ETT tall, body]
 *              [én meta-linje i small, ledd skilt med «·»]
 *
 * Tre elementer utenom meta-linjen. Tallet er reach («4 har spurt»), eller ⚠ + fristdatoen i
 * warning når saken er over frist — da går reach ned i meta-linjen (DD-67: ETT beslutningstall
 * på raden). Meta-linjen: sporets ord halvfet først (DD-61), saksnummeret (DD-59, F16),
 * plattform — eller statuspillen i Saker —, skjerm, Beta, «Svart» (DD-26), «La til mer»
 * (DD-69), «4 uker uten aktivitet» (DD-25), bilde, utkast (DD-65) og alder. Ingen ledd har ramme; bare statuspillen
 * i Saker har flate (F4). Valgt rad skilles på form: flate + linje til venstre (DD-16).
 *
 * Den tilgjengelige etiketten sier det øyet leser, og navngir det raden bare viser med ikon.
 */
export function Rad({ sak, valgt, onSelect, nå, dempet, visStatus, utkast }: { sak: SubmissionSummary; valgt: boolean; onSelect: () => void; nå: Date; dempet?: boolean; /** Noe er skrevet på saken og ikke sendt (DD-65). */ utkast?: boolean; /** Saker-modus (DD-59): statuspillen tar plattformens plass — lista er en blanding av statuser, og det er dem man leter etter. */ visStatus?: boolean }) {
  const spor = SPOR[sak.type];
  const over = sak.overdue;
  const stille = !over && sak.inactiveDays >= INAKTIV_DAGER;
  const plattform = sak.context.os ?? sak.context.platform;
  const fristOm = sak.deadlineAt ? dagerTil(sak.deadlineAt, nå) : null;
  const fristTall = over && sak.deadlineAt ? kortDato(sak.deadlineAt) : null;
  // Utelatt = ikke svart. En sak i Mottatt kan ha fått svar uten statusendring (DD-26,
  // presisert 2026-09-15) — uten merket ser den besvarte saken ubesvart ut i lista.
  const svartAt = sak.repliedAt;
  const svartAlder = svartAt ? alder(svartAt, nå) : null;
  // DD-69: «La til mer» når tillegget er nyere enn svaret OG siste statusendring — det er
  // tillegget saksbehandleren ikke har sett. Terskelen var statusendringen alene så lenge
  // raden ikke bar svartidspunktet; nå gjør den det.
  const sett = Math.max(new Date(sak.statusChangedAt).getTime(), svartAt ? new Date(svartAt).getTime() : 0);
  const laTilMer = sak.lastAddendumAt !== undefined && new Date(sak.lastAddendumAt).getTime() > sett;
  const etikett = [
    `${spor.navn} ${saksnr(sak.number)}: ${sak.text || `(«Ja, jeg også» på kjent feil)`}.`,
    over ? `Over frist${fristTall ? `, svar innen ${fristTall}` : ''}` : null,
    `${reachTekst(sak.reach)}, ${alder(sak.createdAt, nå)} gammel`,
    sak.context.screen ? `skjerm ${skjermNavn(sak.context.screen)}` : null,
    [sak.context.os ?? sak.context.platform, sak.context.appVersion].filter(Boolean).join(' · '),
    sak.programId ? 'i betaprogram' : null,
    sak.hasAttachment ? 'skjermbilde vedlagt' : null,
    utkast ? 'utkast du ikke har sendt' : null,
    svartAlder ? (svartAlder === 'i dag' ? 'svart i dag' : `svart for ${svartAlder} siden`) : null,
    laTilMer ? `innsenderen la til mer ${alder(sak.lastAddendumAt!, nå)}` : null,
    stille ? `4 uker uten aktivitet${fristOm !== null ? `, fristen er om ${fristOm} dager` : ''}` : null,
    `status ${statusEtikett(sak.status, sak.type)}`,
  ]
    .filter(Boolean)
    .join(', ');

  const alleLedd: Array<Ledd | null> = [
    { navn: 'spor', innhold: spor.navn, klasse: 'panel-rad-spor' },
    { navn: 'nr', innhold: saksnr(sak.number) },
    visStatus ? { navn: 'status', innhold: <Pille status={sak.status} type={sak.type} liten /> } : plattform ? { navn: 'plattform', innhold: plattform } : null,
    sak.context.screen ? { navn: 'skjerm', innhold: skjermNavn(sak.context.screen) } : null,
    sak.programId
      ? {
          navn: 'beta',
          innhold: (
            <>
              <Ikon navn="flask-outline" /> Beta
            </>
          ),
        }
      : null,
    svartAlder
      ? {
          navn: 'svart',
          klasse: 'panel-rad-halvfet',
          testid: `svart-${sak.id}`,
          innhold: (
            <>
              <Ikon navn="mail-outline" /> Svart · {svartAlder}
            </>
          ),
        }
      : null,
    laTilMer
      ? {
          navn: 'la-til-mer',
          klasse: 'panel-rad-halvfet',
          testid: `la-til-mer-${sak.id}`,
          innhold: (
            <>
              <Ikon navn="add-outline" /> La til mer · {alder(sak.lastAddendumAt!, nå)}
            </>
          ),
        }
      : null,
    stille
      ? {
          navn: 'stille',
          klasse: 'panel-rad-stille',
          innhold: (
            <>
              <Ikon navn="pause-circle-outline" /> 4 uker uten aktivitet
            </>
          ),
        }
      : null,
    sak.hasAttachment
      ? {
          navn: 'bilde',
          innhold: (
            <>
              <Ikon navn="image-outline" /> bilde
            </>
          ),
        }
      : null,
    utkast
      ? {
          navn: 'utkast',
          klasse: 'panel-rad-halvfet',
          innhold: (
            <>
              <Ikon navn="create-outline" /> Utkast
            </>
          ),
        }
      : null,
    // Over frist er fristdatoen radens tall; reach går hit, og alderen viker (konseptets skjerm 2).
    fristTall ? { navn: 'reach', innhold: reachTekst(sak.reach) } : { navn: 'alder', innhold: alder(sak.createdAt, nå) },
  ];
  const ledd = alleLedd.filter((l): l is Ledd => l !== null);

  return (
    <li>
      <button type="button" className={`panel-rad${valgt ? ' panel-rad-valgt' : ''}${dempet ? ' panel-rad-dempet' : ''}`} aria-label={etikett} aria-current={valgt ? 'true' : undefined} onClick={onSelect} data-testid={`rad-${sak.id}`}>
        {/* DD-61 presisert: ikonet i merkefargen, i egen kolonne — ingen ramme, ingen flate. */}
        <span className="panel-rad-sporikon">
          <Ikon navn={spor.ikon} />
        </span>
        <span className="panel-rad-tekst">{sak.text || <em>«Ja, jeg også» på kjent feil</em>}</span>
        {fristTall ? (
          <span className="panel-rad-tall panel-rad-over">
            <Ikon navn="warning-outline" />
            {fristTall}
          </span>
        ) : (
          <span className="panel-rad-tall">
            {sak.reach > 0 ? (
              <>
                <b>{sak.reach}</b> har spurt
              </>
            ) : (
              'anonym'
            )}
          </span>
        )}
        <span className="panel-rad-meta">
          {ledd.map((l) => (
            <span key={l.navn} className={`panel-rad-ledd${l.klasse ? ` ${l.klasse}` : ''}`} data-testid={l.testid}>
              {l.innhold}
            </span>
          ))}
        </span>
      </button>
    </li>
  );
}
